import type { ReactNode } from "react"
import { X } from "lucide-react"
import { Badge } from "@/components/ui/Badge"
import { Button } from "@/components/ui/Button"
import { flagForCurrency } from "@/components/ui/Flag"
import { Input } from "@/components/ui/Input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/Table"
import { type ChartShape, type Line, type ListProduct, MARKETS, units } from "../listData"

// Same fixed widths as the live page, so the two look alike.
const MARKET_WIDTH = "w-36"
const NUMBER_CELL = "w-24 whitespace-nowrap text-right tabular-nums"
const HEAD = "h-7 px-2 py-0 text-xs"
const SMALL = "h-6 shrink-0 px-2 text-xs"
const DASH = <span className="text-muted-foreground">–</span>

// A stand-in for the stock chart: the same marks the live chart draws.
const SHAPES: Record<Exclude<ChartShape, "none">, string> = {
  falling: "M0,4 L140,12 L280,18",
  jump: "M0,14 L60,16 L60,4 L160,8 L280,12",
  flat: "M0,6 L280,8",
  out: "M0,6 L30,8 L40,20 L280,20",
}
function FakeChart({ shape }: { shape: ChartShape }) {
  if (shape === "none") return null
  return (
    <svg width={280} height={22} className="block" aria-hidden>
      <path d={SHAPES[shape]} fill="none" stroke="var(--primary)" strokeWidth={1.5} />
      <line x1={150} x2={150} y1={0} y2={22} stroke="var(--chart-axis)" strokeDasharray="2 3" />
      {shape === "out" && <circle cx={40} cy={20} r={3} fill="var(--destructive)" />}
    </svg>
  )
}

const number = (value: number | null) => (value === null ? DASH : units(value))

export function DenseHead({ trailing }: { trailing?: ReactNode }) {
  return (
    <TableHeader>
      <TableRow className="hover:bg-transparent">
        <TableHead className={`${HEAD} ${MARKET_WIDTH}`}>Market</TableHead>
        {["Safety", "Need", "Have", "Arriving", "Short", "Suggest"].map((label) => (
          <TableHead key={label} className={`${HEAD} w-24 text-right`}>{label}</TableHead>
        ))}
        <TableHead className={HEAD} />
        {trailing}
      </TableRow>
    </TableHeader>
  )
}

export function MarketLine({ line, trailing }: { line: Line; trailing?: ReactNode }) {
  const market = MARKETS[line.market]
  const Flag = flagForCurrency(market.currency)
  const hasPlan = line.need !== null
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell className={MARKET_WIDTH}>
        <div className="flex items-center gap-2 font-medium">{Flag && <Flag />}{market.name}</div>
      </TableCell>
      <TableCell className={NUMBER_CELL}>{number(line.safety)}</TableCell>
      <TableCell className={NUMBER_CELL}>{hasPlan ? units(line.need!) : <span className="text-xs">no plan</span>}</TableCell>
      <TableCell className={NUMBER_CELL}>{units(line.have)}</TableCell>
      <TableCell className={NUMBER_CELL}>{line.arriving ? <span className="underline decoration-dotted">{units(line.arriving)}</span> : DASH}</TableCell>
      <TableCell className={NUMBER_CELL}>
        {!hasPlan ? DASH : line.short! > 0 ? <span className="font-semibold">{units(line.short!)}</span> : <span className="font-medium text-success">enough</span>}
      </TableCell>
      <TableCell className={NUMBER_CELL}>{hasPlan && line.short! > 0 ? <span className="font-semibold">{units(line.short!)}</span> : DASH}</TableCell>
      <TableCell className="py-0!"><FakeChart shape={line.chart} /></TableCell>
      {trailing}
    </TableRow>
  )
}

export function HeaderLine({ product, children }: { product: ListProduct; children?: ReactNode }) {
  return (
    <div className="flex items-center gap-x-3 border-b bg-primary/5 px-2 py-1 text-sm">
      <span className="shrink-0 tabular-nums">{product.id}</span>
      <span className="min-w-0 flex-1 truncate font-semibold" title={product.name}>{product.name}</span>
      {children}
    </div>
  )
}

interface Props {
  product: ListProduct
  onRemove?: (id: string) => void
}

// One product on the purchase list: header line, then a line per market.
// Drafts are editable, in progress and arriving are read-only.
export default function ListProductCard({ product, onRemove }: Props) {
  const editable = product.status === "draft"
  return (
    <div className="border-b last:border-b-0">
      <HeaderLine product={product}>
        {product.supplier && <span className="shrink-0 font-medium">{product.supplier}</span>}
        {product.moq !== null && <span className="shrink-0 whitespace-nowrap">MOQ {units(product.moq)}</span>}
        {product.justAdded && <Badge variant="info" className="shrink-0">Just added</Badge>}
        {product.status === "in_progress" && <Badge variant="info" className="shrink-0">In progress</Badge>}
        {editable && <Button className={SMALL}>Start purchasing</Button>}
      </HeaderLine>
      <Table className="table-fixed [&_td]:px-2 [&_td]:py-0.5">
        <DenseHead
          trailing={product.status !== "arriving" && (
            <><TableHead className={`${HEAD} w-24 text-right`}>Buy</TableHead><TableHead className={`${HEAD} w-8`} /></>
          )}
        />
        <TableBody>
          {product.lines.map((line) => (
            <MarketLine
              key={line.market}
              line={line}
              trailing={product.status !== "arriving" && (
                <>
                  <TableCell className="w-24 py-0! text-right tabular-nums">
                    {editable ? (
                      <Input
                        className="h-7 rounded-none border-y-0 px-1.5 py-0 text-right tabular-nums shadow-none"
                        defaultValue={units(line.quantity)}
                        aria-label={`Quantity for ${MARKETS[line.market].name}`}
                      />
                    ) : units(line.quantity)}
                  </TableCell>
                  <TableCell className="w-8 p-0! text-center">
                    {editable && (
                      <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Remove" onClick={() => onRemove?.(product.id)}>
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </>
              )}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
